/**
 * The wizard's landing screen.
 *
 * V2 opens on a hub the requester steps off with a "New data request" button rather than
 * dropping them straight into a form. This is the slice-1 equivalent: a single primary
 * action that starts the country-then-wizard flow.
 */
import { Button, makeStyles, shorthands, Title1, tokens } from '@fluentui/react-components';
import { DocumentAdd24Regular as NewRequest, DocumentBulletList24Regular as ViewCases } from '@fluentui/react-icons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/fluent-adapter';

const useStyles = makeStyles({
  page: {
    maxWidth: '880px',
    marginLeft: 'auto',
    marginRight: 'auto',
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXL,
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  muted: { color: tokens.colorNeutralForeground3 },
  cardActions: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: tokens.spacingVerticalM,
  },
  buttonIcon: { display: 'inline-flex', marginRight: tokens.spacingHorizontalXS },
});

interface LandingPageProps {
  onStart: () => void;
  onViewCases: () => void;
}

export function LandingPage({ onStart, onViewCases }: LandingPageProps) {
  const styles = useStyles();

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <Title1>Law Enforcement Portal</Title1>
        <span className={styles.muted}>
          Submit and track legal requests to Microsoft for law-enforcement purposes.
        </span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New data request</CardTitle>
          <CardDescription>
            Start a new request. You will choose the country or region, then work through the
            request step by step.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={styles.cardActions}>
            <Button appearance="primary" onClick={onStart}>
              <span className={styles.buttonIcon}>
                <NewRequest />
              </span>
              New data request
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>My cases</CardTitle>
          <CardDescription>
            View the requests you have already filed and track where each one stands.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={styles.cardActions}>
            <Button appearance="secondary" onClick={onViewCases}>
              <span className={styles.buttonIcon}>
                <ViewCases />
              </span>
              View my cases
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
